import axios, { AxiosResponse } from "axios";
import API_BASE_URL from "src/config/apiConfig";
import { TenantUsage } from "src/layouts/AdminPortal/UsageLimits/TenantUsageMonitor.types";

// Utility to get token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// ========== Interfaces ==========

interface LoginPayload {
  email: string;
  password: string;
}

interface UpdateAdminPayload {
  target_email: string;
  new_user_name: string;
  new_role_name: string;
}

export interface AddAdminPayload {
  user_name: string;
  email: string;
  role_name: "Super Admin" | "Billing";
  password: string;
  confirm_password: string;
}
export interface UpdateTenantUsagePayload {
  tenant_id: number;
  chatbot_query_limit?: number;
  chatbot_token_limit?: number;
  email_quota?: number;
  template_generation_limit?: number;
  template_token_limit?: number;
}
interface ActivityLogPayload {
  start_date: string;
  end_date: string;
}

interface DashboardMetricsPayload {
  start_date: string;
  end_date: string;
}

interface CreateInvoicePayload {
  user_email: string;
  billing_address: string;
  package_name: string;
  package_price: number;
  next_billing_date: string;
  status: string;
  extra_users: number;
  price_per_extra_user: number;
  notes: string;
}

interface PipelineHealthPayload {
  client_name: string;
  start_date: string;
  end_date: string;
}

export interface AdminUser {
  user_name: string;
  email: string;
  user_role: string;
  tenant_name: string;
  signup_date: string;
  plan?: string;
  status: string;
  last_login: string;
}

// ========== Settings API Calls ==========

// Login
export const adminLogin = (payload: LoginPayload) => {
  return axios.post(`${API_BASE_URL}/admins/login`, payload);
};

// Get list of admins
export const getAdminList = () => {
  return axios.post(`${API_BASE_URL}/admins/list`, {}, getAuthHeaders());
};

export const deleteAdmin = (payload: { target_email: string }) => {
  const authHeaders = getAuthHeaders().headers; // ✅ extract just the headers object
  return axios.delete(`${API_BASE_URL}/admins/delete`, {
    data: payload,
    headers: {
      ...authHeaders,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

// Update admin (edit)
export const updateAdmin = (payload: UpdateAdminPayload) => {
  return axios.post(`${API_BASE_URL}/admins/update`, payload, getAuthHeaders());
};

// Add New Admin
export const addAdmin = (payload: AddAdminPayload) => {
  return axios.post(`${API_BASE_URL}/admins/add`, payload, getAuthHeaders());
};

// Get Audit Logs List
export const getAuditLogs = () => {
  const token = localStorage.getItem("admin_token");
  return axios.get(`${API_BASE_URL}/admins/logs`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// ========== Activity Logs API Calls ==========
export const getUserActivityLogs = (payload: ActivityLogPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/user-activity-logs`,
    payload,
    getAuthHeaders()
  );
};

// ========== Dashbaord Logs API Calls ==========
export const getDashboardMetrics = (payload: DashboardMetricsPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/dashboard-metrics`,
    payload,
    getAuthHeaders()
  );
};

// Get current API health status
export const getAPIHealthStatus = () => {
  return axios.get(`${API_BASE_URL}/health-status`, getAuthHeaders());
};

// Trigger a health re-check
export const triggerAPIHealthCheck = () => {
  return axios.get(`${API_BASE_URL}/health-check`, getAuthHeaders());
};

// ========== System Alerts API Call ==========
export const getSystemAlerts = () => {
  return axios.post(
    `${API_BASE_URL}/admins/system-alerts`,
    {},
    getAuthHeaders()
  );
};

// ========== Subscriptions API Call ==========

//Get existing invoices lsit
export const getInvoices = () => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/list`,
    {},
    getAuthHeaders()
  );
};

// Get Subscription Overview cards
export const getSubscriptionOverview = () => {
  return axios.post(
    `${API_BASE_URL}/admins/subscriptions/overview`,
    {},
    getAuthHeaders()
  );
};

//get tenant list for create invoice page dropdowns
export const getTenants = () => {
  return axios.post(`${API_BASE_URL}/admins/tenants`, {}, getAuthHeaders());
};

//create invoice
export const createInvoice = (payload: CreateInvoicePayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/create`,
    payload,
    getAuthHeaders()
  );
};

//view invoice
export const getInvoiceDetails = async (invoice_id: string) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices`,
    { invoice_id },
    getAuthHeaders()
  );
};

export const editInvoice = (payload: {
  invoice_id: string;
  billing_address: string;
  package_name: string;
  package_price: number;
  next_billing_date: string;
  status: string;
  extra_users: number;
  price_per_extra_user: number;
  notes: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/invoices/edit`,
    payload,
    getAuthHeaders()
  );
};

// Delete Invoice API
export const deleteInvoice = (invoice_id: string) => {
  return axios.delete(`${API_BASE_URL}/admins/invoices/delete`, {
    ...getAuthHeaders(),
    data: { invoice_id }, // ✅ Body goes inside `data`
  });
};

// ========== Data Sources API Call ==========

// Get Connected Sources
export const getConnectedSources = () => {
  return axios.get(
    `${API_BASE_URL}/admins/connected-sources`,
    getAuthHeaders()
  );
};

//sync connection
export const syncNow = (payload: {
  user_email: string;
  source_name: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/sync-now`,
    payload,
    getAuthHeaders()
  );
};

//view connection
export const viewConnection = (data: {
  user_email: string;
  source_name: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/view-connection`,
    data,
    getAuthHeaders()
  );
};

export const deleteConnectedSource = (payload: {
  user_email: string;
  source_name: string;
}) => {
  return axios.delete(`${API_BASE_URL}/admins/connected-sources`, {
    ...getAuthHeaders(),
    data: payload, // ✅ This is how you send a body with DELETE
  });
};

// ========== Pipeline Health API Call ==========
export const getPipelineHealthData = (payload: PipelineHealthPayload) => {
  return axios.post(
    `${API_BASE_URL}/admins/pipelines-data`,
    payload,
    getAuthHeaders()
  );
};

// ========== Announcements API Call ==========

// Add this at the bottom of your file or near related endpoints
export const getAnnouncements = () => {
  return axios.get(`${API_BASE_URL}/admins/announcements`, getAuthHeaders());
};

export const createAnnouncement = (payload: {
  title: string;
  content?: string;
  target_audience?: string;
  scheduled_for?: string;
  status: "scheduled" | "published" | "draft";
  url?: string; // ✅ Optional field added
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/announcements/create`,
    payload,
    getAuthHeaders()
  );
};

export const deleteAnnouncement = (payload: { announcement_id: number }) => {
  return axios.delete(`${API_BASE_URL}/admins/announcements/delete`, {
    ...getAuthHeaders(),
    data: payload,
  });
};

export const updateAnnouncement = (payload: {
  announcement_id: number;
  title: string;
  content: string;
  target_audience: string;
  scheduled_for: string | null;
  status: string;
  url?: string; // ✅ Optional field added
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/announcements/update`,
    payload,
    getAuthHeaders()
  );
};

// ========== Users API Call ==========
export const getAllUsers = (): Promise<AxiosResponse<AdminUser[]>> => {
  return axios.get(`${API_BASE_URL}/admins/get-all-users`, getAuthHeaders());
};

export const addPlatformUser = (payload: {
  user_name: string;
  user_email: string;
  tenant_email: string;
  status: string;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/add-user`,
    payload,
    getAuthHeaders()
  );
};

export const performUserAction = (payload: {
  user_id: number;
  user_name?: string;
  status?: string;
  delete?: boolean;
  suspend?: boolean;
}) => {
  return axios.post(
    `${API_BASE_URL}/admins/perform-action-on-user`,
    payload,
    getAuthHeaders()
  );
};

export const resetUserPassword = async (user_email: string) => {
  return axios.post(
    `${API_BASE_URL}/admins/reset-user-password`,
    { user_email },
    getAuthHeaders()
  );
};

// ========== Usage & Limits API Call ==========

// Fetch daily tenant usage
export const getTenantsDailyUsage = async () => {
  const apiKey = localStorage.getItem("admin_token");

  const res = await axios.post(
    `${API_BASE_URL}/admins/get-tenants-daily-usage`,
    {},
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "x-api-key": apiKey,
      },
    }
  );

  return res.data;
};

// Update tenant usage limits
export const updateTenantUsageLimits = async (
  payload: UpdateTenantUsagePayload
) => {
  const apiKey = localStorage.getItem("admin_token");
  if (!apiKey) {
    throw new Error("Missing admin token. Please log in again.");
  }

  const response = await fetch(
    `${API_BASE_URL}/admin/update-tenant-daily-usage?token=${apiKey}`, // ✅ use token query param
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload), // ✅ schema from curl
    }
  );

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to update tenant usage limits: ${errText}`);
  }

  return response.json();
};



